import { BookOpen01Icon, Clock01Icon, LockIcon, Tick02Icon } from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'

import { getBlockHintTexts } from '@/features/journeys/lib/blockHintText'
import { useBlocker } from '@/shared/hooks/useBlocker'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { CardHint } from '@/shared/ui/CardHint'
import { FeaturedListCardStrip, featuredListCardClassName } from '@/shared/ui/FeaturedListCardStrip'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { QUIZ_STATUS_LABELS } from '../constants/status'
import type { QuizListItem } from '../hooks/useQuizzesListView'
import { getQuizScoreLabel } from '../lib/quizScoreLabel'

const progressVariantForStatus = (
  status: QuizListItem['uiStatus']
): 'primary' | 'progress' | 'success' | 'locked' => {
  if (status === 'progress') return 'progress'
  if (status === 'success') return 'success'
  if (status === 'locked' || status === 'expired') return 'locked'
  return 'primary'
}

const buttonVariantForStatus = (
  status: QuizListItem['uiStatus']
): 'primary' | 'progress' | 'success-ghost' | 'secondary' | undefined => {
  if (status === 'available') return 'primary'
  if (status === 'progress') return 'progress'
  if (status === 'success' || status === 'expired') return 'success-ghost'
  if (status === 'locked') return 'secondary'
  return undefined
}

type QuizListCardProps = {
  quiz: QuizListItem
  featured?: boolean
}

const ctaLabelForQuiz = (
  quiz: QuizListItem,
  featured?: boolean
): string | null => {
  const { uiStatus } = quiz

  if (uiStatus === 'locked') return 'Detalhes'
  if (uiStatus === 'expired') return null
  if (uiStatus === 'success') return 'Revisitar'
  if (uiStatus === 'progress') return 'Continuar'
  if (uiStatus === 'available') return featured ? 'Participar' : 'Iniciar'

  return null
}

export const QuizListCard = ({ quiz, featured = false }: QuizListCardProps) => {
  const { showBlockAlert, checkBlockCondition } = useBlocker()
  const { navigateTo } = useNavigation()

  const {
    id,
    uid,
    name,
    description,
    icon,
    point_reward,
    coin_reward,
    uiStatus,
    progressPercent,
    _progress
  } = quiz

  const blockResult = checkBlockCondition(quiz._blockCondition)
  const blockHints = !blockResult.unlocked
    ? getBlockHintTexts(blockResult.unmet)
    : []

  const scoreLabel =
    uiStatus === 'success' ? getQuizScoreLabel(quiz, _progress) : null

  const iconUrl = _.get(icon, 'file.url')
  const isLocked = uiStatus === 'locked'
  const isExpired = uiStatus === 'expired'

  const navigate = () => {
    if (blockResult.unlocked && !isExpired) {
      navigateTo(`/quizzes/${uid || id}/iniciar`)
      return
    }

    if (isExpired) {
      navigateTo(`/quizzes/${uid || id}/iniciar`)
      return
    }

    if (blockResult.firstUnmet) {
      showBlockAlert(blockResult.firstUnmet)
    }
  }

  const ctaLabel = ctaLabelForQuiz(quiz, featured)
  const ctaVariant = buttonVariantForStatus(uiStatus)
  const showCta = ctaLabel && ctaVariant

  return (
    <Card
      flush={featured}
      onClick={navigate}
      className={clsx(
        'cursor-pointer',
        featured ? featuredListCardClassName('quiz') : 'shadow-sm',
        isLocked && 'bg-gray-100',
        isExpired && 'opacity-90'
      )}
    >
      {featured && (
        <FeaturedListCardStrip label="Quiz em destaque" tone="quiz" />
      )}

      <Card.Body>
        <div className="flex items-stretch gap-2 sm:gap-3">
          {/* Thumbnail — aspect-square padrão (MASTER §7 L8), não `self-stretch` */}
          <div className="flex aspect-square w-20 shrink-0 self-start overflow-hidden rounded-lg">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className={clsx(
                  'h-full w-full object-cover',
                  (isLocked || isExpired) && 'opacity-65 grayscale'
                )}
              />
            ) : (
              <div
                className={clsx(
                  'flex min-h-0 w-full flex-1 items-center justify-center bg-brand-surface',
                  (isLocked || isExpired) && 'opacity-65 grayscale'
                )}
              >
                {isLocked ? (
                  <LockIcon
                    size={24}
                    variant="duotone"
                    className="text-gray-400"
                  />
                ) : (
                  <BookOpen01Icon
                    size={24}
                    variant="duotone"
                    className="text-primary opacity-45"
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            {/* Dois eixos, dois badges: status e recência. O `new` só aparece
                dentro da janela de 1h. `available` sem badge — status-padrão,
                tag é só pra exceção (progresso/bloqueio/conclusão/encerramento). */}
            <div className="flex flex-wrap items-center gap-1.5">
              {uiStatus !== 'available' && (
                <Badge variant={uiStatus} size="sm" showIcon={false}>
                  {QUIZ_STATUS_LABELS[uiStatus]}
                </Badge>
              )}
              {quiz.isNew && (
                <Badge variant="new" size="sm">
                  Novo
                </Badge>
              )}
            </div>

            <h3
              className={clsx(
                'm-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900',
                (isLocked || isExpired) && 'text-gray-500'
              )}
            >
              {name}
            </h3>

            {description &&
              uiStatus !== 'success' &&
              uiStatus !== 'locked' && (
                <p className="m-0 line-clamp-2 text-2xs leading-snug text-gray-700">
                  {description}
                </p>
              )}

            {((point_reward ?? 0) > 0 || (coin_reward ?? 0) > 0) && (
              <RewardBadge.Group>
                {(point_reward ?? 0) > 0 && (
                  <RewardBadge type="xp" value={point_reward!} variant="inline" />
                )}
                {(coin_reward ?? 0) > 0 && (
                  <RewardBadge
                    type="coins"
                    value={coin_reward!}
                    variant="inline"
                  />
                )}
              </RewardBadge.Group>
            )}

            <div className="mt-0.5 flex items-center gap-2">
              <ProgressBar
                className="min-w-0 flex-1"
                value={isLocked ? 0 : progressPercent}
                variant={progressVariantForStatus(uiStatus)}
                size="sm"
              />
              {!isLocked && (
                <span className="shrink-0 text-2xs font-bold leading-none text-gray-500">
                  {progressPercent}%
                </span>
              )}
            </div>
          </div>

          {/* Rail — largura via token (MASTER §7 L8), não número solto */}
          <div className="flex min-h-20 w-[var(--rail-w-sm)] shrink-0 flex-col">
            <div className="flex flex-1 flex-col items-end justify-center gap-0.5 text-right">
              {uiStatus === 'success' && scoreLabel && (
                <CardHint
                  icon={<Tick02Icon size={14} aria-hidden />}
                  className="font-semibold text-status-success"
                >
                  {scoreLabel}
                </CardHint>
              )}

              {uiStatus === 'progress' && blockHints.length === 0 && (
                <CardHint
                  icon={<Clock01Icon size={14} aria-hidden />}
                  className="font-semibold text-status-progress"
                >
                  Em andamento
                </CardHint>
              )}

              {blockHints.map(hint => (
                <CardHint
                  key={hint}
                  icon={<LockIcon size={14} aria-hidden />}
                >
                  {hint}
                </CardHint>
              ))}
            </div>

            <div className="flex flex-col items-stretch">
              {showCta && (
                <Button
                  variant={ctaVariant}
                  size="sm"
                  className="w-full justify-center font-bold!"
                  onClick={e => {
                    e.stopPropagation()
                    navigate()
                  }}
                >
                  {ctaLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
